import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Standard material consumption per job, per service type, in decimal units.
export const ServiceMaterials = ({ canWrite }) => {
  const [services, setServices] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState([]);

  const load = () => {
    api.get("/config/services").then(({ data }) => setServices(data));
    api.get("/catalog").then(({ data }) => setCatalog(data));
  };
  useEffect(() => {
    load();
  }, []);

  const skuName = (id) => catalog.find((c) => c.id === id)?.name || "Unknown SKU";
  const skuUnit = (id) => catalog.find((c) => c.id === id)?.unit || "";

  const openSvc = (svc) => {
    setOpen(svc.id);
    setDraft(
      (svc.materials || []).map((m) => ({
        catalog_sku_id: m.catalog_sku_id,
        quantity_per_job: String(m.quantity_per_job ?? 0),
        use_ppf_sqft: !!m.use_ppf_sqft,
      }))
    );
  };

  const save = async (svc) => {
    try {
      await api.put(`/config/services/${svc.id}`, {
        name: svc.name,
        default_labor_cost: svc.default_labor_cost || 0,
        requires_before_after: !!svc.requires_before_after,
        warranty_months: svc.warranty_months || 0,
        warranty_type: svc.warranty_type || "",
        duration_days: svc.duration_days || 1,
        active: svc.active !== false,
        materials: draft
          .filter((d) => d.catalog_sku_id)
          .map((d) => ({
            catalog_sku_id: d.catalog_sku_id,
            quantity_per_job: d.use_ppf_sqft ? 0 : Number(d.quantity_per_job || 0),
            use_ppf_sqft: d.use_ppf_sqft,
          })),
      });
      toast.success(`${svc.name} consumption template saved`);
      setOpen(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="mb-6 border-brand-line shadow-none" data-testid="service-materials-card">
      <CardHeader>
        <CardTitle className="text-base">Material consumption per service</CardTitle>
        <p className="mt-1 text-xs text-slate-500">
          Standard quantity consumed per job, in the material&apos;s own unit and in decimals (25 ml
          of a 50 ml bottle = 25). Tick &quot;from PPF sqft&quot; to take the quantity from the car
          model instead. Materials are still ordered and received in full units.
        </p>
      </CardHeader>
      <CardContent className="divide-y divide-slate-100 p-0">
        {services.map((svc) => (
          <div key={svc.id} className="p-4" data-testid={`svc-materials-${svc.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-brand-ink">{svc.name}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(svc.materials || []).length === 0 && (
                    <span className="text-xs text-slate-400">No materials configured</span>
                  )}
                  {(svc.materials || []).map((m) => (
                    <Badge
                      key={m.catalog_sku_id}
                      className="border-0 bg-slate-100 text-slate-600"
                      data-testid={`svc-mat-${svc.id}-${m.catalog_sku_id}`}
                    >
                      {skuName(m.catalog_sku_id)} ·{" "}
                      {m.use_ppf_sqft
                        ? "from PPF sqft"
                        : `${m.quantity_per_job} ${skuUnit(m.catalog_sku_id)}`}
                    </Badge>
                  ))}
                </div>
              </div>
              {canWrite && (
                <Button
                  size="sm"
                  variant="outline"
                  className="min-h-[44px]"
                  data-testid={`edit-svc-materials-${svc.id}`}
                  onClick={() => (open === svc.id ? setOpen(null) : openSvc(svc))}
                >
                  {open === svc.id ? "Close" : "Edit template"}
                </Button>
              )}
            </div>

            {open === svc.id && (
              <div className="mt-4 space-y-3 rounded-md bg-slate-50/70 p-3">
                {draft.map((line, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-2">
                    <Select
                      value={line.catalog_sku_id}
                      onValueChange={(v) =>
                        setDraft((d) =>
                          d.map((x, i) => (i === idx ? { ...x, catalog_sku_id: v } : x))
                        )
                      }
                    >
                      <SelectTrigger
                        className="min-w-[220px] flex-1"
                        data-testid={`mat-sku-select-${svc.id}-${idx}`}
                      >
                        <SelectValue placeholder="Select material" />
                      </SelectTrigger>
                      <SelectContent>
                        {catalog.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name} ({c.unit})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      className="w-28 min-h-[44px] text-right"
                      inputMode="decimal"
                      disabled={line.use_ppf_sqft}
                      placeholder="Qty"
                      data-testid={`mat-qty-input-${svc.id}-${idx}`}
                      value={line.use_ppf_sqft ? "" : line.quantity_per_job}
                      onChange={(e) =>
                        setDraft((d) =>
                          d.map((x, i) =>
                            i === idx ? { ...x, quantity_per_job: e.target.value } : x
                          )
                        )
                      }
                    />
                    <span className="text-xs text-slate-500">{skuUnit(line.catalog_sku_id)}</span>
                    <label className="flex items-center gap-1.5 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        data-testid={`mat-sqft-toggle-${svc.id}-${idx}`}
                        checked={line.use_ppf_sqft}
                        onChange={(e) =>
                          setDraft((d) =>
                            d.map((x, i) =>
                              i === idx ? { ...x, use_ppf_sqft: e.target.checked } : x
                            )
                          )
                        }
                      />
                      from PPF sqft
                    </label>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-rose-600"
                      data-testid={`mat-remove-${svc.id}-${idx}`}
                      onClick={() => setDraft((d) => d.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`add-mat-line-${svc.id}`}
                    onClick={() =>
                      setDraft((d) => [
                        ...d,
                        { catalog_sku_id: "", quantity_per_job: "", use_ppf_sqft: false },
                      ])
                    }
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" /> Add material
                  </Button>
                  <Button
                    size="sm"
                    data-testid={`save-svc-materials-${svc.id}`}
                    onClick={() => save(svc)}
                  >
                    Save template
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
